import React from "react";
import { X, Ruler, Hand, MoveHorizontal, MoveVertical } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";

const SizeGuideDialog = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const g = t.size_guide;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="size-guide-dialog"
        className="bg-[#0A0A0A] border border-[#00A651]/30 text-white rounded-none max-w-3xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        <div className="relative p-6 md:p-10">
          <DialogClose
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-white/15 hover:border-[#00A651] hover:text-[#00A651] transition-colors z-10"
            data-testid="size-guide-close"
            aria-label={t.close}
          >
            <X className="w-4 h-4" />
          </DialogClose>

          {/* Kicker */}
          <div className="inline-flex items-center gap-2 bg-[#00A651] text-black font-condensed uppercase tracking-widest text-[10px] px-2.5 py-1 mb-5">
            <Ruler className="w-3 h-3" />
            Fit · 4keeperz
          </div>

          {/* Header */}
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="font-brush text-4xl md:text-5xl text-[#00A651] leading-tight uppercase">
              {g.title}
            </DialogTitle>
            <DialogDescription className="text-white/60 font-condensed uppercase tracking-widest text-xs">
              {g.subtitle}
            </DialogDescription>
          </DialogHeader>

          <p className="mt-6 text-white/80 text-sm md:text-base leading-relaxed">
            {g.intro}
          </p>

          {/* Measurement illustration */}
          <div className="mt-6 border border-white/10 bg-black/40 p-5 flex items-center gap-5">
            <div
              className="shrink-0 w-20 h-20 border border-[#00A651]/30 flex items-center justify-center"
              aria-hidden="true"
            >
              <Hand className="w-10 h-10 text-[#00A651]" />
            </div>
            <div className="flex-1 space-y-2">
              <div className="text-[11px] text-[#00A651] font-condensed uppercase tracking-widest">
                {g.measure_hint}
              </div>
              <ol className="text-white/70 text-sm space-y-1.5">
                {g.measure_steps.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[#00A651] font-display text-base leading-none shrink-0">
                      {i + 1}.
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Table */}
          <div className="mt-6 border border-white/10 overflow-hidden">
            <table className="w-full text-left" data-testid="size-guide-table">
              <thead>
                <tr className="bg-black/60 border-b border-white/10">
                  {g.columns.map((c, i) => (
                    <th
                      key={c}
                      className="font-condensed uppercase tracking-widest text-[10px] md:text-[11px] text-white/50 px-3 md:px-4 py-3"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {i === 1 && (
                          <MoveHorizontal className="w-3 h-3 text-[#00A651]" />
                        )}
                        {i === 2 && (
                          <MoveVertical className="w-3 h-3 text-[#00A651]" />
                        )}
                        {c}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {g.rows.map((row) => (
                  <tr
                    key={row.size}
                    className="border-b border-white/5 last:border-0 hover:bg-black/40 transition-colors"
                    data-testid={`size-row-${row.size}`}
                  >
                    <td className="px-3 md:px-4 py-3">
                      <div className="inline-flex items-center justify-center min-w-[42px] px-2 py-1 border border-[#00A651]/40 bg-[#00A651]/5 font-display text-xl md:text-2xl text-[#00A651] leading-none">
                        {row.size}
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3 text-white/90 font-display tabular-nums text-sm md:text-base">
                      {row.hand_width}
                    </td>
                    <td className="px-3 md:px-4 py-3 text-white/90 font-display tabular-nums text-sm md:text-base">
                      {row.hand_length}
                    </td>
                    <td className="px-3 md:px-4 py-3 text-white/60 text-xs md:text-sm">
                      {row.audience}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tip */}
          <p className="mt-5 text-xs md:text-sm text-white/60 italic border-l-2 border-[#00A651] pl-4">
            {g.tip}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SizeGuideDialog;
